/**
 * Shared auth session types and response normalisation.
 * Used by the client login service and the same-origin proxy route.
 */

export interface Credentials {
  identifier: string;
  password: string;
}

export interface AuthUser {
  id?: string;
  email?: string;
  name?: string;
}

export interface Session {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function readString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return null;
}

function toAuthUser(raw: unknown): AuthUser | null {
  const user = asRecord(raw);
  if (!user) return null;

  const idValue = user.id;
  const id =
    typeof idValue === 'string' || typeof idValue === 'number' ? String(idValue) : undefined;

  return {
    id,
    email: typeof user.email === 'string' ? user.email : undefined,
    name:
      readString(user.name, user.full_name, user.username, user.login) ?? undefined,
  };
}

/**
 * Login response shapes differ between backends (`token`, `access`, nested
 * `data`). Normalising here keeps that churn out of the UI.
 */
export function toSession(payload: unknown): Session {
  const root = asRecord(payload);
  if (!root) return { token: null, refreshToken: null, user: null };

  const data = asRecord(root.data) ?? root;

  const token = readString(data.token, data.access, data.access_token, data.accessToken, data.key);
  const refreshToken = readString(data.refresh, data.refresh_token, data.refreshToken);

  return {
    token,
    refreshToken,
    user: toAuthUser(data.user),
  };
}

/** Prefer username, then email — for header chrome and welcome copy. */
export function getSessionLabel(session: Session | null | undefined): string | null {
  if (!session?.token) return null;
  return readString(session.user?.name, session.user?.email) ?? 'Operator';
}

/** True when `/api/token/` returned an SMS OTP challenge instead of JWTs. */
export function isOtpChallenge(payload: unknown): boolean {
  const root = asRecord(payload);
  if (!root) return false;
  const data = asRecord(root.data) ?? root;
  return data.otp_required === true && typeof data.otp_token === 'string';
}

/** SMS one-time-code step the backend asks for instead of JWTs (`otp_required: true`). */
export interface OtpChallenge {
  otpToken: string;
  /** Masked phone the code went to, e.g. `+1 ••• ••• 1234`. */
  sentTo: string | null;
  expiresInSeconds: number;
  message: string | null;
}

/** Codes are valid for 5 minutes unless the backend says otherwise. */
export const DEFAULT_OTP_EXPIRES_IN = 300;

export function toOtpChallenge(payload: unknown): OtpChallenge | null {
  if (!isOtpChallenge(payload)) return null;
  const root = asRecord(payload) ?? {};
  const data = asRecord(root.data) ?? root;
  const otpToken = readString(data.otp_token);
  if (!otpToken) return null;
  const expires = Number(data.expires_in);

  return {
    otpToken,
    sentTo: readString(data.otp_sent_to),
    expiresInSeconds: Number.isFinite(expires) && expires > 0 ? Math.floor(expires) : DEFAULT_OTP_EXPIRES_IN,
    message: readString(data.message),
  };
}
